// 頁型模板④：純顯示頁（32 筆 route）。
//
// 原系統對應：只有 `.ibox-title` + `.ibox-content` 的唯讀內容區，沒有表單也沒有 DataTables
// （例 `WEB-INF/views/customer/tsgh/tsghStudyHistoryData.jsp:311` 的 `.well` 區塊）。
//
// 支援的 scenario（§5.2）：default（純顯示頁只有這一個預設狀態）。
// 共通資料狀態仍然要正確：loading / empty / error / unauthorized。
// 預設集以外的狀態（create／edit／validation-error…）在**頁型層級**一次標為
// Not Applicable：這個頁型沒有任何寫入動作，不是「還沒實作」。
//
// 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】

import { type ReactElement, useState } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { ContentPanel, FormGrid, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { LegacyAsideBlocks } from '@/mocks/legacy-aside-notes';
import { t } from '@/messages/zh-TW';
import { pageBehavior } from './page-behaviors';
import {
  columnsFor,
  initialValues,
  legacyContentFor,
  legacyContractFieldsFor,
  resolveLegacyMessageForAction,
  toPayload,
  useDataSource,
  useList,
  useMessageKeyOverride,
} from '@/mocks';
import { LegacyContractFields } from './LegacyContractFields';
import { LegacyStaticContent } from './LegacyStaticContent';
import { DialogHost, FormWrap, TemplateError, toUiKey } from './shared';
import type { PageTemplateProps } from './types';

export function DisplayPage({ route }: PageTemplateProps): ReactElement {
  return (
    <DialogHost route={route}>
      <DisplayBody route={route} />
    </DialogHost>
  );
}

function DisplayBody({ route }: PageTemplateProps) {
  const pageId = route.pageId;
  // 批次 17（B8-3，Gitea issue #16）：display 頁型的整頁自訂 body（customer 高風險/服務量
  // 設定頁：級聯＋可編輯表，泛用「唯讀 dl＋契約欄位」模板不匹配）。批次 13 complexBody hook
  // 先例（ComplexFormPage/CrudListPage 同款早退），放在 hooks 之前。未登錄頁零影響。
  const complexBehavior = pageBehavior(pageId);
  if (complexBehavior?.complexBody !== undefined) {
    return complexBehavior.complexBody({ pageId });
  }
  // 原系統這一頁若是「靜態內容頁」（內容寫死在 JSP，2 頁），它根本沒有資料來源。
  const staticContent = legacyContentFor(pageId);
  const columns = columnsFor(pageId);
  // FC-3：原系統這一頁若在主體區有實測到的欄位（17 頁），那就不是唯讀 <dl> 該處理的東西，
  // 而是一張可填寫的表單。改畫契約欄位，並把值真的送進 dataSource.update（criteria C5-h）。
  // legacyFormFor() 的退路（display 頁型的舊主表單路徑）已刪除——
  // legacy-fallback-dead-path.test.ts 逐頁驗證過全站 156 頁沒有一頁需要它。
  const contractFields = legacyContractFieldsFor(pageId);
  // 唯讀頁只需要一筆。pageSize:1 讓 empty scenario（0 筆）自然變成「查無資料」。
  const { data, loading, error, reload } = useList(pageId, { page: 1, pageSize: 1 });
  const record = data?.rows[0] ?? null;

  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // FC-5 R4（C1-c‴）：見 CrudListPage.tsx 同名變數的說明。
  const messageKeyOverride = useMessageKeyOverride();
  const [values, setValues] = useState<Record<string, string>>(() =>
    initialValues(contractFields ?? [], pageId),
  );

  /** 儲存：把使用者填的欄位值送進資料源。這顆鈕真的會打端點，所以標 `data-effect`（D-1）。 */
  async function handleSave() {
    try {
      const payload = toPayload(values, contractFields, pageId);
      await dataSource.update(pageId, String(record?.id ?? ''), payload);
      await dialogs.info(resolveLegacyMessageForAction(pageId, 'success', 'save')?.text ?? t('state.saveSuccess'));
    } catch {
      await dialogs.alert(
        resolveLegacyMessageForAction(pageId, 'failure', 'save', messageKeyOverride)?.text ??
          t('error.generic'),
      );
    }
  }

  return (
    // 模板只負責內容區：全站外框（ApplicationShell／Navbar／SidebarMenu／Breadcrumb）
    // 由路由層的 AppLayout 提供，模板再包一層會變成殼中殼。
    // 這個 <div> 是整頁的根節點，data-ui-id 就是 §10.1 說的 pageId 本身。
    <div data-ui-id={pageId}>
      {/*
        FC-4：原系統該頁的對話框（trigger tier A–D）。
        trigger 鈕帶 `data-modal-trigger`（FUNCTIONAL-CONTRACT.md D-3），
        對話框本身條件渲染 —— 唯讀 GET 抽不到是**正確結果**，React 側證據一律走
        `tests/react/extract-modals.spec.ts` 的互動擷取。
        三級（global／shared／unique）都由這裡畫：本專案沒有 AppLayout 這一層，
        而三級的內容全部來自同一份 legacy 實測資料，分開手抄只會多一次抄錯的機會。
      */}
      <LegacyContractModals pageId={pageId} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title={route.title}
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {/*
          靜態內容頁擺在最前面：它沒有資料來源，所以 loading／empty／error 都不適用。
          放在 error／empty 之後的話，`?scenario=empty` 會讓一頁寫死的影片索引顯示
          「查無資料」——那是錯的。
        */}
        {staticContent !== undefined ? (
          <LegacyStaticContent pageId={pageId} items={staticContent} />
        ) : error !== null ? (
          <TemplateError
            uiId={`${pageId}-error`}
            error={error}
            onRetry={reload}
            pageId={pageId}
            {...(messageKeyOverride === undefined ? {} : { messageKeyOverride })}
          />
        ) : record === null ? (
          <p
            data-ui-id={`${pageId}-content-empty`}
            className="py-6 text-center text-ink-muted text-sm"
          >
            {loading ? t('table.loadingRecords') : t('table.empty')}
          </p>
        ) : contractFields !== undefined ? (
          // FC-3：原系統該頁真正的欄位（帶 name）。取代 legacyFormFor() 的舊路徑——
          // 後者要求「整張表單所有控制項都是自由文字」，28 頁只放行 1 頁（legacy-forms.ts:46-52）。
          // 批次 36（GNL-005-b，issue #38）：欄位＋儲存鈕包 <form> 鏡像 legacy（attr 由
          // LEGACY_FORM_SPECS 鏡像；onSubmit preventDefault，送出語意由 handleSave 承擔）。
          <>
            <FormWrap pageId={pageId}>
              <FormGrid uiId={`${pageId}-content-grid`} columns={2}>
                <LegacyContractFields
                  pageId={pageId}
                  fields={contractFields}
                  values={values}
                  disabled={loading}
                  onChange={(name, next) => {
                    setValues((current) => ({ ...current, [name]: next }));
                  }}
                />
              </FormGrid>
              <div data-ui-id={`${pageId}-actions`} className="flex flex-wrap gap-2">
                <Button
                  uiId={`${pageId}-actions-save`}
                  variant="primary"
                  size="sm"
                  type="button"
                  disabled={loading}
                  // D-1：這顆真的會打 `dataSource.update(pageId, id, values)`（handleSave）。
                  dataEffect={`update:${pageId}`}
                  onClick={() => {
                    void handleSave();
                  }}
                >
                  {t('action.save')}
                </Button>
              </div>
            </FormWrap>
          </>
        ) : (
          // ponytail: 用原生 <dl>，不為唯讀頁另外做一個 DescriptionList 元件 ——
          // 目前只有這一個頁型會用到。升級路徑：第二個使用者出現時再抽到第二層。
          <dl data-ui-id={`${pageId}-content-list`} className="grid gap-x-6 gap-y-2 md:grid-cols-2">
            {columns.map((column) => {
              const fieldKey = toUiKey(column.key);
              const value = record[column.key];
              return (
                <div
                  key={column.key}
                  data-ui-id={`${pageId}-content-field-${fieldKey}`}
                  className="flex flex-col gap-1"
                >
                  <dt
                    data-ui-id={`${pageId}-content-label-${fieldKey}`}
                    className="text-ink-muted text-sm"
                  >
                    {column.label}
                  </dt>
                  <dd data-ui-id={`${pageId}-content-value-${fieldKey}`} className="text-sm">
                    {value === undefined || value === null ? '' : String(value)}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </ContentPanel>
      {/* 批次 44（#42 GNL-005-f）：legacy 說明區（display 頁型；form-plan-form-dispatch
          計畫表單/測驗兩段說明）。panel 之後渲染；未命中＝零影響。 */}
      <LegacyAsideBlocks pageId={pageId} />
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading} />
    </div>
  );
}
